// src/pages/profile-settings/doctor/PracticeTeamTasks.jsx
import React from "react";
import { Navigate, useNavigate } from "react-router-dom";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  getPracticeTeamDelegatedTasks,
  getPracticeTeamMemberIdentity,
  getPracticeTeamPriorityMeta,
  getPracticeTeamTaskSections,
  getPracticeTeamTaskTypeLabel,
  getPracticeTeamUnavailableCapabilityAreas,
} from "./practiceTeamWorkspaceModel.js";
import { usePracticeTeamAccess } from "./practiceTeamMemberAccess.js";
import PracticeTeamWorkspaceHeader from "./PracticeTeamWorkspaceHeader.jsx";

function TaskPriorityChip({ priority }) {
  const meta = getPracticeTeamPriorityMeta(priority);

  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-label-medium ${meta.chipClassName}`}>
      <span className={`h-2 w-2 rounded-full ${meta.dotClassName}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function TaskStatusChip({ status }) {
  return (
    <span className="inline-flex w-fit max-w-max shrink-0 self-start rounded-full bg-[rgba(var(--clr-on-surface),0.08)] px-3 py-1 text-label-medium text-on-surface-var">
      {status}
    </span>
  );
}

function DelegatedTaskRow({ task, isLast = false }) {
  return (
    <article className={["py-4", isLast ? "" : "border-b border-[rgba(var(--clr-outline),0.32)]"].join(" ")}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <TaskPriorityChip priority={task.priority} />
            <span className="text-label-medium text-on-surface-var">
              {getPracticeTeamTaskTypeLabel(task.taskType)}
            </span>
          </div>
          <h3 className="mt-3 text-title-small text-on-surface">{task.title}</h3>
          <p className="mt-1 max-w-[48rem] text-body-medium text-on-surface-var">{task.summary}</p>
        </div>
        <TaskStatusChip status={task.status} />
      </div>

      <dl className="mt-4 grid gap-3 text-body-small text-on-surface-var md:grid-cols-4">
        <div>
          <dt className="text-label-medium text-on-surface-var">Clinician</dt>
          <dd className="mt-1 text-on-surface">{task.originatingClinician}</dd>
        </div>
        <div>
          <dt className="text-label-medium text-on-surface-var">Workflow</dt>
          <dd className="mt-1 text-on-surface">{task.originatingWorkflow}</dd>
        </div>
        <div>
          <dt className="text-label-medium text-on-surface-var">
            {task.completedDate ? "Completed" : "Due"}
          </dt>
          <dd className="mt-1 text-on-surface">{task.completedDate || task.dueDate}</dd>
        </div>
        <div>
          <dt className="text-label-medium text-on-surface-var">Permission</dt>
          <dd className="mt-1 break-words text-on-surface">{task.permissionRequired}</dd>
        </div>
      </dl>
    </article>
  );
}

function TaskSection({ section }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-title-small text-on-surface">{section.label}</h2>
        <p className="mt-1 text-body-medium text-on-surface-var">{section.description}</p>
      </div>

      <SettingsCard fullWidth className="px-5 py-2 md:px-6">
        {section.tasks.length > 0 ? (
          section.tasks.map((task, index) => (
            <DelegatedTaskRow key={task.id} task={task} isLast={index === section.tasks.length - 1} />
          ))
        ) : (
          <div className="py-4">
            <h3 className="text-title-medium text-on-surface">{section.emptyTitle}</h3>
            <p className="mt-2 text-body-medium text-on-surface-var">{section.emptyBody}</p>
          </div>
        )}
      </SettingsCard>
    </section>
  );
}

export default function PracticeTeamTasks() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { auth, invite, resolving, allowed } = usePracticeTeamAccess({ requiredSectionId: "tasks" });
  const identity = React.useMemo(() => getPracticeTeamMemberIdentity(invite, auth?.user), [auth?.user, invite]);
  const delegatedTasks = React.useMemo(() => {
    return getPracticeTeamDelegatedTasks(invite?.role, identity);
  }, [identity, invite?.role]);
  const taskSections = React.useMemo(() => getPracticeTeamTaskSections(delegatedTasks), [delegatedTasks]);
  const capabilityAreas = React.useMemo(() => {
    return getPracticeTeamUnavailableCapabilityAreas(invite?.role, {
      exclude: ["assignment", "tasks"],
    });
  }, [invite?.role]);

  if (resolving) {
    return <RouteFallback label="Loading Practice Team..." />;
  }

  if (!allowed) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--afrimed-shell-background))] text-[rgb(var(--clr-on-surface))]">
      <PracticeTeamWorkspaceHeader section="tasks" invite={invite} identity={identity} />
      <SettingsScreenShell className="[--rail-pad:12px] lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        <TopBarCenter
          title={t("settings.practiceTeam.tasks.title", "My Tasks")}
          onBack={() => navigate("/practice-team/welcome")}
        />

        <div className="space-y-8">
          <p className="-mt-2 text-body-medium text-on-surface-var">
            {t(
              "settings.practiceTeam.tasks.subtitle",
              "Delegated work assigned to you will appear here."
            )}
          </p>

          <div className="space-y-6">
            {taskSections.map((section) => (
              <TaskSection key={section.id} section={section} />
            ))}
          </div>

          <section className="space-y-3">
            <h2 className="text-title-small text-on-surface">
              {t("settings.practiceTeam.tasks.comingTitle", "Locked and coming sections")}
            </h2>
            <p className="max-w-[48rem] text-body-medium text-on-surface-var">
              {t(
                "settings.practiceTeam.tasks.comingBody",
                "These sections are shown from the Practice Team capability model and remain closed until later workflow slices."
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {capabilityAreas.map((area) => (
                <SaveChip
                  key={area.id}
                  label={area.label}
                  tabIndex={-1}
                  aria-disabled="true"
                  className="pointer-events-none min-h-8 px-3 py-1 text-label-large"
                />
              ))}
            </div>
          </section>
        </div>
      </SettingsScreenShell>
    </div>
  );
}
